import React, { useState } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, MessageSquare, History } from "lucide-react";
import { Btn, EstadoBadge, Field, Select, TextArea } from "./ui";
import { MediaPreview } from "./MediaPreview";
import { errMsg, fmtDate, fmtSize, triggerDownload } from "./api";

const CHECKS = [
  ["autorizaciones", "Las personas que aparecen autorizaron la grabación y publicación"],
  ["privacidad", "No vulnera la privacidad (menores, domicilios, situaciones sensibles)"],
  ["procedencia", "La procedencia del material es verificable y es del colaborador"],
  ["datos_personales", "No expone datos personales (DNI, patentes, teléfonos, direcciones)"],
];

const DECISIONES = [
  { value: "aprobado", label: "Aprobar" },
  { value: "correcciones", label: "Requiere correcciones" },
  { value: "rechazado", label: "Rechazar" },
  { value: "pendiente", label: "Volver a pendiente" },
];

export const EnvioDetail = ({ client, envio, onClose, onChanged }) => {
  const [e, setE] = useState(envio);
  const [chk, setChk] = useState({ autorizaciones: false, privacidad: false, procedencia: false, datos_personales: false, ...(envio.checklist || {}) });
  const [estado, setEstado] = useState("aprobado");
  const [comentario, setComentario] = useState("");
  const [vis, setVis] = useState("colaboradores");
  const [recre, setRecre] = useState(false);
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const allOk = Object.values(chk).every(Boolean);

  const decidir = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/envios/${e.id}/decision`, { estado, comentario, checklist: chk, visibilidad: vis, es_recreacion: recre });
      setE(data);
      setComentario("");
      toast.success("Decisión registrada y colaborador notificado");
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const comentar = async () => {
    if (!nota.trim()) return;
    try {
      const { data } = await client.post(`/envios/${e.id}/comentarios`, { texto: nota });
      setE((x) => ({ ...x, comentarios_internos: [...(x.comentarios_internos || []), data] }));
      setNota("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const descargar = async () => {
    try {
      const { data } = await client.get(`/files/${e.file_id}/link`);
      triggerDownload(`${data.url}&dl=1`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-start justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="envio-detail">
      <div className="bg-white w-full max-w-5xl my-6" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex justify-between items-start gap-3 p-4 border-b">
          <div>
            <div className="flex items-center gap-2 mb-1"><EstadoBadge estado={e.estado} testid="envio-detail-estado" /><span className="text-[10px] font-headings uppercase tracking-wider text-[#0A2463]">{e.categoria}</span></div>
            <h3 className="font-headings uppercase text-xl text-[#0A192F]">{e.titulo}</h3>
          </div>
          <button onClick={onClose} data-testid="envio-detail-close"><X /></button>
        </div>
        <div className="grid lg:grid-cols-5 gap-0">
          <div className="lg:col-span-3 p-4 space-y-4 border-b lg:border-b-0 lg:border-r">
            <MediaPreview client={client} fid={e.file_id} tipo={e.tipo} className="w-full max-h-[55vh] object-contain bg-black" />
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Colaborador</dt><dd>{e.nombre}<br /><span className="text-xs text-gray-500">{e.email}</span></dd></div>
              <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Provincia / localidad</dt><dd>{e.localidad}, {e.provincia}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Grabado</dt><dd>{fmtDate(e.fecha_grabacion)} · {e.lugar}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Archivo</dt><dd>{e.filename} · {fmtSize(e.size)}</dd></div>
              <div className="col-span-2"><dt className="text-[10px] uppercase tracking-wider text-gray-500">Descripción</dt><dd className="whitespace-pre-line">{e.descripcion}</dd></div>
              <div className="col-span-2 text-xs text-emerald-700">✓ Declaró tener autorización para publicar</div>
            </dl>
            <Btn variant="ghost" onClick={descargar} data-testid="envio-descargar-original">Descargar original</Btn>
            <div>
              <h4 className="font-headings uppercase text-sm tracking-wide text-[#0A192F] mb-2 flex items-center gap-2"><History size={15} /> Registro de decisiones</h4>
              <ol className="space-y-2 text-sm" data-testid="envio-historial">
                {(e.historial || []).map((h, i) => (
                  <li key={i} className="border-l-2 border-gray-300 pl-3">
                    <EstadoBadge estado={h.estado} /> <span className="text-xs text-gray-500">{h.por} · {new Date(h.fecha).toLocaleString("es-AR")}</span>
                    {h.comentario && <p className="text-[#374151] mt-0.5">{h.comentario}</p>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="lg:col-span-2 p-4 space-y-5">
            <div>
              <h4 className="font-headings uppercase text-sm tracking-wide text-[#0A192F] mb-2 flex items-center gap-2"><ShieldCheck size={15} /> Verificación</h4>
              <div className="space-y-2">
                {CHECKS.map(([k, l]) => (
                  <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!chk[k]} onChange={(ev) => setChk((c) => ({ ...c, [k]: ev.target.checked }))} className="mt-1 accent-[#0A2463]" data-testid={`check-${k}`} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-3 p-3 bg-gray-50 border">
              <Field label="Decisión">
                <Select value={estado} options={DECISIONES} onChange={(ev) => setEstado(ev.target.value)} data-testid="decision-estado" />
              </Field>
              {estado === "aprobado" && (
                <>
                  <Field label="Visible para">
                    <Select value={vis} options={[{ value: "colaboradores", label: "Solo colaboradores" }, { value: "publico", label: "Colaboradores y biblioteca pública" }]} onChange={(ev) => setVis(ev.target.value)} data-testid="decision-visibilidad" />
                  </Field>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={recre} onChange={(ev) => setRecre(ev.target.checked)} data-testid="decision-recreacion" /> Es montaje / recreación digital</label>
                </>
              )}
              <Field label={estado === "rechazado" || estado === "correcciones" ? "Mensaje al colaborador *" : "Mensaje al colaborador (opcional)"}>
                <TextArea rows={3} value={comentario} onChange={(ev) => setComentario(ev.target.value)} placeholder="Se le envía por email y lo ve en su portal" data-testid="decision-comentario" />
              </Field>
              {estado === "aprobado" && !allOk && <p className="text-xs text-amber-700">Marcá las 4 verificaciones para poder aprobar.</p>}
              <Btn busy={busy} onClick={decidir} disabled={estado === "aprobado" && !allOk} className="w-full" data-testid="decision-submit">Registrar decisión</Btn>
            </div>
            <div>
              <h4 className="font-headings uppercase text-sm tracking-wide text-[#0A192F] mb-2 flex items-center gap-2"><MessageSquare size={15} /> Comentarios internos</h4>
              <p className="text-xs text-gray-500 mb-2">Solo los ve el equipo de moderación.</p>
              <div className="space-y-2 mb-2 max-h-48 overflow-y-auto" data-testid="envio-comentarios">
                {(e.comentarios_internos || []).map((c) => (
                  <div key={c.id} className="text-sm bg-yellow-50 border border-yellow-200 p-2"><span className="text-xs text-gray-500">{c.por} · {new Date(c.fecha).toLocaleString("es-AR")}</span><p>{c.texto}</p></div>
                ))}
              </div>
              <div className="flex gap-2">
                <TextArea rows={2} value={nota} onChange={(ev) => setNota(ev.target.value)} placeholder="Nota interna…" data-testid="comentario-input" />
                <Btn variant="ghost" onClick={comentar} data-testid="comentario-submit">Agregar</Btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvioDetail;
